'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { ArrowLeft, ArrowRight, type LucideIcon } from 'lucide-react';
import { useLabsStore } from '../../pack-context';
import { BrandButton } from './brand-button';

/**
 * StepNav + BackButton — the paired step-control footer for every content screen.
 *
 * Each content screen shows a SECONDARY "← Back" button paired with its PRIMARY "Continue" at the
 * bottom — Back on the LEFT, the primary on the RIGHT, same row, reading as a matched pair. Both get
 * an identical min-width + centered content + the same px/py/text, so they are EXACTLY the same size.
 * The primary label defaults to "Continue" (every screen from M1 onward uses that single label).
 *
 * "Back" / "Continue" are INVARIANT chrome — they never name a vertical, so they stay hardcoded here
 * rather than coming from the pack's copy.
 *
 * Determinism: pure presentational — no Math.random()/Date.now().
 */

/**
 * BackButton — the secondary "← Back" control. Neutral outline/ghost matched to BrandButton's
 * EXACT dimensions (min-w-[180px], px-6 py-4, text-[18px], rounded-fs-button) with a leading
 * ArrowLeft, so Back and the primary are the same size.
 */
export function BackButton(t0) {
  const $ = _c(4);
  const {
    onClick,
    label: t1
  } = t0;
  const label = t1 === undefined ? "Back" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ArrowLeft className="h-5 w-5" strokeWidth={2} />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== label || $[2] !== onClick) {
    t3 = <button className="inline-flex min-w-0 cursor-pointer items-center justify-center gap-2 rounded-fs-button border border-fs-border bg-fs-surface px-6 py-4 font-medium text-[18px] text-fs-text-muted leading-none transition-colors hover:bg-fs-surface-subtle hover:text-fs-text sm:min-w-[180px]" onClick={onClick} type="button">{t2}{label}</button>;
    $[1] = label;
    $[2] = onClick;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}

/**
 * StepNav — the paired footer row: secondary Back on the LEFT, primary on the RIGHT, same row, same
 * size. `onBack` defaults to the active vertical's store `back()`; `nextLabel` defaults to
 * "Continue". Callers usually pass only `onNext` (+ optional `nextDisabled`/`nextIcon`). If `onNext`
 * is omitted the right side is empty (Back-only). The primary routes through `BrandButton` (native
 * `disabled`).
 */
export function StepNav(t0) {
  const $ = _c(10);
  const {
    onBack,
    nextLabel: t1,
    onNext,
    nextDisabled,
    nextIcon
  } = t0;
  const nextLabel = t1 === undefined ? "Continue" : t1;
  const storeBack = useLabsStore(_temp);
  const t2 = onBack ?? storeBack;
  let t3;
  if ($[0] !== t2) {
    t3 = <BackButton onClick={t2} />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== nextDisabled || $[3] !== nextIcon || $[4] !== nextLabel || $[5] !== onNext) {
    t4 = onNext ? <BrandButton className="min-w-0 whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-40 sm:min-w-[180px]" disabled={nextDisabled} icon={nextIcon ?? ArrowRight} iconPosition="trailing" onClick={onNext}>{nextLabel}</BrandButton> : null;
    $[2] = nextDisabled;
    $[3] = nextIcon;
    $[4] = nextLabel;
    $[5] = onNext;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <div className="flex w-full items-center justify-between gap-3">{t3}{t4}</div>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function _temp(s) {
  return s.back;
}